import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { hasAccessRights } from '../../../../../shared/functions/Functions';
import { useSelector } from 'react-redux';
import { IAllStates } from '../../../../../store/rootReducer';
import { useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AccessRightEnum } from '../../../../../enum/AccessRightEnum';
import { CampaignManagement } from '../../dashboard/components/CampaignManagement';
import { ICampaignManagement } from '../../dashboard/components/dto/ICampaignManagement';
import { CampaignTeamCard } from '../../dashboard/components/CampaignTeamCard';
import { CampaignSupervisorCard } from '../../dashboard/components/CampaignSupervisorCard';
import { User } from '../../../../../model/user/User';
import { CampaignOverviewDetail } from './CampaignOverviewDetail';
import CardLayout from '../../../../../shared/components/card/CardLayout';
import { BootstrapBreakpoints } from '../../../../../enum/BootstrapBreakpoints';
import { OptionService } from '../../options/service';
import { IParams } from '../../../../../model/shared/IParams';
import { useParams } from 'react-router-dom';
import { ErrorToast } from '../../../../../utils/toasters';
import { IBundle } from '../../../../../model/options/IBundle';
import { DashUsersSkeleton } from '../../dashboard/components/CampaignDashboardSkeletons';
import { CampaignOverviewDetailSkeleton } from './CampaignOverviewDetailSkeleton';

interface IDashboardOverviewCardProps {
    info?: ICampaignManagement;
    setUpdate: Dispatch<SetStateAction<boolean>>;
    supervisors: User[];
    isManagementLoading?: boolean;
    isSupervisorsLoading?: boolean;
}

export function DashboardRightSide({
    info,
    setUpdate,
    supervisors,
    isManagementLoading = false,
    isSupervisorsLoading = false,
}: IDashboardOverviewCardProps) {
    const { t } = useTranslation();
    const { id } = useParams<keyof IParams>();
    const { campaign } = useSelector((state: IAllStates) => state.selectedCampaign);
    const isMobile = useMediaQuery(`(max-width: ${BootstrapBreakpoints.MD}px)`);
    const [bundle, setBundle] = useState<IBundle | undefined>(undefined);

    const canSeeSupervisors =
        hasAccessRights(AccessRightEnum.CAN_VIEW_BOOKING) || hasAccessRights(AccessRightEnum.CAN_VIEW_CONTRACTS);

    useEffect(() => {
        if (!id) {
            return;
        }
        OptionService.getBundle(id)
            .then(response => {
                if (response.id) {
                    setBundle(response);
                }
            })
            .catch(err => ErrorToast(err));
    }, [id]);

    return (
        <>
            <CardLayout classCard="overflow-hidden">
                <div className="d-flex flex-column">
                    {(campaign?.isLongTerm || !!campaign?.parent?.id) && isMobile && (
                        <div className="diagonal-text">
                            <span className="diagonal-text-sm">
                                {t(
                                    `brand.campaign.briefing.overview_card.${
                                        campaign?.isLongTerm ? 'long_term' : 'linked'
                                    }`
                                )}
                            </span>
                        </div>
                    )}
                </div>

                {campaign ? <CampaignOverviewDetail /> : <CampaignOverviewDetailSkeleton />}

                {canSeeSupervisors && (isSupervisorsLoading || !!supervisors.length) && (
                    <div className="pb-2 border-bottom mb-2">
                        {isSupervisorsLoading ? (
                            <DashUsersSkeleton />
                        ) : (
                            <CampaignSupervisorCard supervisors={supervisors} />
                        )}
                    </div>
                )}

                <div className="pb-2 border-bottom mb-2">
                    {isManagementLoading ? (
                        <DashUsersSkeleton />
                    ) : (
                        <CampaignManagement managementInfo={info} setUpdateManagement={setUpdate} />
                    )}
                </div>

                {isManagementLoading ? (
                    <div className="pb-2 border-bottom mb-2">
                        <DashUsersSkeleton footer="end" />
                    </div>
                ) : (
                    <>
                        {info?.isSupplier && !!info?.clientUsers?.length && (
                            <div className="pb-2 border-bottom mb-2">
                                <CampaignTeamCard
                                    setUpdateManagement={setUpdate}
                                    managementInfo={info}
                                    isLongTermDash={true}
                                />
                            </div>
                        )}
                        {!info?.isSupplier && !!info?.assignedUsers?.length && (
                            <div className="pb-2 border-bottom mb-2">
                                <CampaignTeamCard
                                    setUpdateManagement={setUpdate}
                                    managementInfo={info}
                                    isLongTermDash={true}
                                />
                            </div>
                        )}
                    </>
                )}
            </CardLayout>
            {/* <div className="mt-3">{bundle?.isEnabled && <CampaignBundleSupplierCard />}</div> */}
        </>
    );
}
